import React, { useContext, useReducer, useMemo } from 'react'

import {
  createCheckoutState,
  createShopifyStores,
  getCustomDomains,
  validateStores,
} from '../lib/shopify'

import { getSessionStorageSizeVariantKey } from '../utils'

import reducers from '../reducers'

export const ShopifyContext = React.createContext({})

export const ShopifyProvider = (props) => {
  const { stores } = props

  validateStores(stores)

  const initialState = {
    storeSubdomain: (stores.find((s) => s.default) || stores[0])?.subdomain,
    customDomains: getCustomDomains(stores),
    stores: createShopifyStores(stores),
    checkouts: createCheckoutState(stores),
    sizeVariants: {},
    currency: {
      code: 'EUR',
      symbol: '€',
    },
    currencyPopIn: null,
  }

  const [state, dispatch] = useReducer(reducers, initialState)

  const selectStore = (data) => {
    const storeSubdomains = Object.keys(state.checkouts)
    const storeExists = storeSubdomains.find((store) => store === data)
    if (storeExists) dispatch({ type: 'SELECT_STORE', data })
    else throw new Error('The store is not found.')
  }

  const replaceCheckout = (checkout, segmentId = '') => {
    const storeSubdomains = Object.keys(state.checkouts)
    const storeExists = storeSubdomains.find(
      (store) => store === checkout?.storeSubdomain,
    )

    if (storeExists) {
      dispatch({
        type: 'REPLACE_CHECKOUT',
        data: {
          ...checkout,
          customAttributes: [
            ...checkout.customAttributes.map(({ key, value }) => ({
              key,
              value,
            })),
            { key: 'segment-clientID', value: segmentId },
            // { key: 'google-clientID', value: googleId },
          ],
        },
      })
    } else throw new Error('The store is not found.')
  }

  const setSizeVariants = (data) => dispatch({ type: 'SET_SIZE_VARIANT', data })

  const replaceSizeVariants = (productType, gender, sizeVariantIndex) => {
    const { sizeVariants, storeSubdomain } = state

    sizeVariants[productType] = sizeVariants[productType] || {}
    sizeVariants[productType][gender] = sizeVariantIndex

    sessionStorage.setItem(
      getSessionStorageSizeVariantKey(),
      JSON.stringify(sizeVariants),
    )

    dispatch({
      type: 'REPLACE_SIZE_VARIANT',
      data: sizeVariants,
    })
  }

  const setCurrency = (data) => dispatch({ type: `UPDATE_CURRENCY`, data })

  const setCurrencyPopIn = (data) =>
    dispatch({ type: `UPDATE_CURRENCY_POPIN`, data })

  const value = useMemo(
    () => ({
      ...state,
      selectStore,
      replaceCheckout,
      setSizeVariants,
      replaceSizeVariants,
      setCurrency,
      setCurrencyPopIn,
    }),
    [state],
  )

  return <ShopifyContext.Provider {...props} value={value} />
}

export const useShopify = () => {
  const context = useContext(ShopifyContext)

  if (context === undefined)
    throw new Error(`useShopify must be used within a ShopifyProvider`)

  return context
}
